import type { CommandInput, CommandResult, UndoCommandInput, UndoOutcome } from '@mystiq/contracts';
import { useState } from 'react';
import { useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { ErrorLine } from './ui.tsx';

type Context = CommandInput['context'];

/**
 * "Ask Mystiq" for the open or selected items: a short instruction ("get rid of
 * these", "move these to Research", "write a brief from these") is planned by
 * AI and run on exactly those items, with a result per item and Undo. Nothing
 * runs when the request is unclear; a question comes back instead.
 */
export function CommandBar({
  ws,
  context,
  placeholder,
  onReplacement,
}: {
  ws: string;
  /** Read at the moment of asking (the selection may have changed since render); throws to refuse with a message. */
  context: () => Context;
  placeholder: string;
  /** Applies a rewrite of the selected text; returns a message when it could not be applied. */
  onReplacement?: (text: string) => string | null;
}) {
  const api = useApi();
  const [text, setText] = useState('');
  const [result, setResult] = useState<CommandResult | null>(null);
  const [undone, setUndone] = useState<UndoOutcome[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [refused, setRefused] = useState<Error | null>(null);
  const run = useWorkspaceMutation(ws, (input: CommandInput) => api.runCommand(ws, input));
  const undo = useWorkspaceMutation(ws, (input: UndoCommandInput) => api.undoCommand(ws, input));

  const failed = result?.kind === 'done' ? result.outcomes.filter((outcome) => outcome.status === 'failed') : [];
  const canUndo = result?.kind === 'done' && (result.undo.steps.length > 0 || result.undo.cancelOperationIds.length > 0);

  return (
    <div className="command-bar">
      <form
        className="inline-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setUndone(null);
          setNote(null);
          setRefused(null);
          let current: Context;
          try {
            current = context();
          } catch (error) {
            setResult(null);
            setRefused(error instanceof Error ? error : new Error('This cannot be asked about.'));
            return;
          }
          const outcome = await run.mutateAsync({ text: text.trim(), context: current }).catch(() => null);
          if (!outcome) return;
          setResult(outcome);
          if (outcome.kind === 'done') {
            setText('');
            if (outcome.replacement && onReplacement) setNote(onReplacement(outcome.replacement));
          }
        }}
      >
        <input className="input" aria-label="Ask Mystiq" placeholder={placeholder} value={text} maxLength={2000} onChange={(event) => setText(event.target.value)} />
        <button className="button" type="submit" disabled={run.isPending || !text.trim()}>
          {run.isPending ? 'Working…' : 'Ask'}
        </button>
      </form>
      {result?.kind === 'clarify' && (
        <p className="meta" role="status">
          <span className="state">{result.question}</span>
        </p>
      )}
      {result?.kind === 'done' && (
        <div className="stack" role="status">
          <p className="meta">
            {result.summary || 'Done.'}
            {failed.length ? (
              <>
                {' '}
                <span className="state">{failed.length === result.outcomes.length ? 'It could not be done.' : `${failed.length} of ${result.outcomes.length} steps failed.`}</span>{' '}
                {[...new Set(failed.map((outcome) => outcome.message))].join(' ')}
              </>
            ) : null}
            {result.outcomes.some((outcome) => outcome.operationId) ? ' The new note will appear when it is written.' : ''}
            {note ? (
              <>
                {' '}
                <span className="state">{note}</span>
              </>
            ) : null}
          </p>
          {canUndo && !undone && (
            <div>
              <button className="link-button" disabled={undo.isPending} onClick={async () => setUndone(await undo.mutateAsync(result.undo as UndoCommandInput).catch(() => null))}>
                Undo
              </button>
            </div>
          )}
          {undone && (
            <p className="meta">
              {undone.every((outcome) => outcome.status === 'undone') ? 'Undone.' : undone.map((outcome) => outcome.message).join(' ')}
            </p>
          )}
        </div>
      )}
      <ErrorLine error={refused ?? run.error ?? undo.error} />
    </div>
  );
}
